import { bibleVerses } from '../../i18n/modules/bibleVerses';
import { supabase } from '../../lib/supabase';
import { getDeviceTimeZone } from './deviceTimeZone';

// =====================================================
// TYPES AND INTERFACES
// =====================================================

export interface UserStreak {
  id: string;
  user_id: string;
  current_streak: number;
  longest_streak: number;
  last_activity_date: string;
  grace_period_used: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserXP {
  id: string;
  user_id: string;
  total_xp: number;
  current_level: number;
  xp_to_next_level: number;
  rank: 'Beginner' | 'Scholar' | 'Teacher' | 'Sage';
  quizzes_completed?: number;
  current_streak?: number;
  achievements_count?: number;
  created_at: string;
  updated_at: string;
}

export interface XPTransaction {
  id: string;
  user_id: string;
  amount: number;
  source: string;
  description?: string;
  created_at: string;
}

export interface Achievement {
  id: string;
  achievement_key: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  xp_reward: number;
  required_progress: number;
  is_hidden: boolean;
  created_at: string;
  /** PGS achievement ID (set after Play Console setup) */
  pgs_id?: string;
  /** PGS achievement type */
  pgs_type?: 'standard' | 'incremental';
  /** Total steps for incremental PGS achievements */
  pgs_steps?: number;
}

export interface UserAchievement {
  id: string;
  user_id: string;
  achievement_id: string;
  current_progress: number;
  completed: boolean;
  completed_at?: string;
  unlocked_at?: string;
  title?: string;
  created_at: string;
  updated_at: string;
  achievement?: Achievement;
}

export interface Reward {
  id: string;
  reward_key: string;
  name: string;
  description: string;
  reward_type: string;
  icon: string;
  created_at: string;
}

export interface UserReward {
  id: string;
  user_id: string;
  reward_id: string;
  is_active: boolean;
  acquired_at: string;
  reward?: Reward;
}

export interface DailyVerse {
  id: string;
  verse_reference: string;
  verse_text: string;
  display_date: string;
  created_at: string;
}

export interface UserDailyVerse {
  id: string;
  user_id: string;
  verse_id: string;
  completed: boolean;
  completed_at?: string;
  created_at: string;
}

export interface GamificationProfile {
  user_id: string;
  current_streak: number;
  longest_streak: number;
  last_activity_date: string;
  total_xp: number;
  current_level: number;
  xp_to_next_level: number;
  rank: string;
  quizzes_completed: number;
  completed_achievements: number;
  total_achievements: number;
}

export interface StreakCalendarDay {
  activity_date: string;
  activity_type: string;
  has_activity: boolean;
}

export interface XPLeaderboardEntry {
  user_id: string;
  username: string;
  total_xp: number;
  current_level: number;
  rank: string;
  position?: number;
  xp_to_next_level?: number;
  avatar_url?: string | null;
  quiz_count?: number;
  streak_count?: number;
  level?: number;
}

// =====================================================
// CORE XP FUNCTIONS
// =====================================================

/**
 * Get user's current XP data
 */
export async function getUserXP(userId: string): Promise<UserXP | null> {
  try {
    const { data, error } = await supabase
      .from('user_xp')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) {
      if (error.code === '42P01' || error.details?.includes('does not exist')) {
        // Table doesn't exist - return default XP data
        console.warn('user_xp table does not exist - returning default XP data');
        return {
          id: `default-${userId}`,
          user_id: userId,
          total_xp: 0,
          current_level: 1,
          xp_to_next_level: 100,
          rank: 'Beginner',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }
      console.error('Error fetching user XP:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in getUserXP:', error);
    return null;
  }
}

/**
 * Award XP to a user
 */
export async function awardXP(
  userId: string, 
  amount: number, 
  source: string, 
  description?: string
): Promise<boolean> {
  try {
    const { error } = await supabase.rpc('award_user_xp', {
      p_user_id: userId,
      p_amount: amount,
      p_source: source,
      p_description: description
    });

    if (error) {
      console.error('Error awarding XP:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in awardXP:', error);
    return false;
  }
}

/**
 * Format XP number for display
 */
export function formatXP(xp: number): string {
  if (xp >= 1000000) {
    return `${(xp / 1000000).toFixed(1)}M`;
  } else if (xp >= 1000) {
    return `${(xp / 1000).toFixed(1)}K`;
  }
  return xp.toString();
}

/**
 * Get emoji for achievement category
 */
export function getAchievementCategoryEmoji(category: string): string {
  const categoryEmojis: { [key: string]: string } = {
    quiz: '🧠',
    streak: '🔥',
    knowledge: '📚',
    social: '👥',
    challenge: '🏆',
    milestone: '⭐',
    special: '🎁',
    daily: '📅',
    weekly: '📊',
    monthly: '🗓️'
  };
  return categoryEmojis[category] || '🏅';
}

/**
 * Get rank from level number
 */
export function getRankFromLevel(level: number): string {
  if (level >= 50) return 'Sage';
  if (level >= 25) return 'Teacher';
  if (level >= 10) return 'Scholar';
  return 'Beginner';
}

/**
 * Get user's current streak data
 */
export async function getUserStreak(userId: string): Promise<UserStreak | null> {
  try {
    const { data, error } = await supabase
      .from('user_streaks')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) {
      if (error.code === '42P01' || error.details?.includes('does not exist')) {
        // Table doesn't exist - return default streak data
        console.warn('user_streaks table does not exist - returning default streak data');
        return {
          id: `default-${userId}`,
          user_id: userId,
          current_streak: 0,
          longest_streak: 0,
          last_activity_date: new Date().toISOString().split('T')[0],
          grace_period_used: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }
      console.error('Error fetching user streak:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in getUserStreak:', error);
    return null;
  }
}

/**
 * Get user's complete gamification profile
 */
export async function getUserGamificationProfile(userId: string): Promise<GamificationProfile | null> {
  try {
    // Get user XP data
    const xpData = await getUserXP(userId);
    if (!xpData) {
      console.error('No XP data found for user:', userId);
      return null;
    }

    // Get user streak data
    const streakData = await getUserStreak(userId);
    if (!streakData) {
      console.error('No streak data found for user:', userId);
      return null;
    }

    // Get achievements count and completed solo quizzes
    const [achievements, catalog, quizResults] = await Promise.all([
      getUserAchievements(userId),
      getAchievements(),
      supabase
        .from('singleplayer_results')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
    ]);

    if (quizResults.error) {
      console.error('Error fetching quiz count:', quizResults.error);
    }

    const completedAchievements = achievements.filter(a => a.completed).length;
    const quizzesCompleted = quizResults.count || 0;
    const totalAchievements = catalog.length;

    const profile: GamificationProfile = {
      user_id: userId,
      current_streak: streakData.current_streak,
      longest_streak: streakData.longest_streak,
      last_activity_date: streakData.last_activity_date,
      total_xp: xpData.total_xp,
      current_level: xpData.current_level,
      xp_to_next_level: xpData.xp_to_next_level,
      rank: xpData.rank,
      quizzes_completed: quizzesCompleted,
      completed_achievements: completedAchievements,
      total_achievements: totalAchievements
    };

    return profile;
  } catch (error) {
    console.error('Error in getUserGamificationProfile:', error);
    return null;
  }
}

const mapAchievementRow = (row: Record<string, any>): Achievement => ({
  id: String(row.id),
  achievement_key: row.achievement_key,
  name: row.name,
  description: row.description,
  category: row.category,
  icon: row.icon,
  xp_reward: row.xp_reward ?? 0,
  required_progress: row.required_progress ?? 1,
  is_hidden: row.is_hidden ?? false,
  created_at: row.created_at ?? '',
  pgs_id: row.pgs_id ?? undefined,
});

/**
 * The achievement catalog a player can still earn, in display order. The rows live
 * in the `achievements` table (same 28 keys as the Play Games catalog); the
 * evaluator keeps progress per user in `user_achievements`.
 */
export async function getAchievements(): Promise<Achievement[]> {
  try {
    const { data, error } = await supabase
      .from('achievements')
      .select('id, achievement_key, name, description, category, icon, xp_reward, required_progress, is_hidden, created_at, pgs_id')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });
    if (error) {
      console.error('Error fetching achievements:', error);
      return [];
    }
    return (data ?? []).map(mapAchievementRow);
  } catch (error) {
    console.error('Error in getAchievements:', error);
    return [];
  }
}

const mapUserAchievementRow = (row: Record<string, any>): UserAchievement => ({
  id: row.id,
  user_id: row.user_id,
  achievement_id: String(row.achievement_id),
  current_progress: row.current_progress ?? row.progress ?? 0,
  completed: Boolean(row.completed),
  completed_at: row.completed_at ?? undefined,
  created_at: row.created_at ?? '',
  updated_at: row.updated_at ?? '',
  achievement: row.achievement ? mapAchievementRow(row.achievement) : undefined,
});

export async function getUserAchievements(userId: string): Promise<UserAchievement[]> {
  try {
    const { data, error } = await supabase
      .from('user_achievements')
      .select(`
        id, user_id, achievement_id, current_progress, progress, completed, completed_at, created_at, updated_at,
        achievement:achievements (
          id, achievement_key, name, description, category, icon, xp_reward, required_progress, is_hidden, is_active, created_at
        )
      `)
      .eq('user_id', userId);

    if (error) {
      if (error.code === '42P01' || error.details?.includes('does not exist')) {
        console.warn('user_achievements table does not exist');
        return [];
      }
      console.error('Error fetching user achievements:', error);
      return [];
    }

    // Rows for retired catalog entries stay in the table but are not shown.
    return (data ?? [])
      .filter((row: any) => row.achievement?.is_active !== false)
      .map(mapUserAchievementRow);
  } catch (error) {
    console.error('Error in getUserAchievements:', error);
    return [];
  }
}

export interface AchievementEvaluation extends UserAchievement {
  achievement: Achievement;
  /** True only on the call that unlocked it; XP has been awarded server-side. */
  newly_completed: boolean;
}

/**
 * Recompute every achievement for the user from their play history, persist the
 * progress, and return the full state. Call after a quiz is recorded and XP
 * awarded, and when opening the achievements screen. Idempotent.
 */
export async function evaluateAchievements(userId: string): Promise<AchievementEvaluation[]> {
  try {
    const { data, error } = await supabase.rpc('evaluate_user_achievements', {
      p_user_id: userId,
      // Streak days follow the device's calendar; the server remembers the zone per profile.
      p_timezone: getDeviceTimeZone(),
    });
    if (error) {
      console.error('Error evaluating achievements:', error);
      return [];
    }
    return (data ?? []).map((row: Record<string, any>) => ({
      id: `${userId}:${row.achievement_id}`,
      user_id: userId,
      achievement_id: String(row.achievement_id),
      current_progress: row.current_progress ?? 0,
      completed: Boolean(row.completed),
      completed_at: row.completed_at ?? undefined,
      created_at: '',
      updated_at: '',
      newly_completed: Boolean(row.newly_completed),
      achievement: mapAchievementRow(row),
    }));
  } catch (error) {
    console.error('Error in evaluateAchievements:', error);
    return [];
  }
}

export const newlyUnlockedAchievements = (rows: AchievementEvaluation[]): AchievementEvaluation[] =>
  rows.filter((row) => row.newly_completed);

/**
 * Check and award achievements based on quiz completion
 */
export async function checkQuizAchievements(
  userId: string,
  quizData: {
    score: number;
    totalQuestions: number;
    timeSpent: number;
    category?: string;
    difficulty?: string;
    questionScores?: Array<{
      format_type: string;
      percentage: number;
      timeSpent?: number;
    }>;
  }
): Promise<UserAchievement[]> {
  try {
    // For now, return empty array - achievements will be processed by the backend
    // This function exists to maintain compatibility, but we can add True/False specific logic
    const newAchievements: UserAchievement[] = [];
    
    // True/False specific achievement checks
    if (quizData.questionScores) {
      const trueFalseQuestions = quizData.questionScores.filter(q => q.format_type === 'true_false');
      
      if (trueFalseQuestions.length > 0) {
        console.log(`[GamificationService] Processing ${trueFalseQuestions.length} True/False questions for achievements`);
        
        // Check for Lightning Judge achievement (quick True/False answers)
        const quickTrueFalse = trueFalseQuestions.filter(q => q.timeSpent && q.timeSpent < 5 && q.percentage === 100);
        if (quickTrueFalse.length >= 5) {
          console.log(`[GamificationService] User ${userId} achieved Lightning Judge progress: ${quickTrueFalse.length} quick correct True/False answers`);
        }
        
        // Check for Truth Seeker achievement (consecutive correct True/False)
        const correctTrueFalse = trueFalseQuestions.filter(q => q.percentage === 100);
        if (correctTrueFalse.length >= 10) {
          console.log(`[GamificationService] User ${userId} achieved Truth Seeker progress: ${correctTrueFalse.length} consecutive correct True/False answers`);
        }
        
        // Check for Binary Master achievement (perfect True/False scores)
        if (correctTrueFalse.length > 0) {
          console.log(`[GamificationService] User ${userId} made progress on Binary Master: ${correctTrueFalse.length} perfect True/False answers`);
        }
      }
    }
    
    return newAchievements;
  } catch (error) {
    console.error('Error in checkQuizAchievements:', error);
    return [];
  }
}

/**
 * Check and award achievements based on social activities
 */
export async function checkSocialAchievements(
  userId: string,
  socialData: {
    action: string;
    targetUserId?: string;
    groupId?: string;
  }
): Promise<UserAchievement[]> {
  try {
    // For now, return empty array - achievements will be processed by the backend
    // This function exists to maintain compatibility
    return [];
  } catch (error) {
    console.error('Error in checkSocialAchievements:', error);
    return [];
  }
}

/**
 * Record daily activity for streak tracking
 */
export async function recordDailyActivity(
  userId: string,
  activityType: string = 'quiz'
): Promise<boolean> {
  try {
    // For now, return true - activity recording will be handled by the backend
    // This function exists to maintain compatibility
    console.log(`[GamificationService] Recording daily activity for user: ${userId}, type: ${activityType}`);
    return true;
  } catch (error) {
    console.error('Error in recordDailyActivity:', error);
    return false;
  }
}

/**
 * Calculate True/False specific rewards and bonuses
 */
export function calculateTrueFalseRewards(questionScores: Array<{
  format_type: string;
  percentage: number;
  timeSpent?: number;
  score?: number;
}>): {
  bonusXP: number;
  achievements: string[];
  streaks: { type: string; count: number }[];
} {
  const trueFalseQuestions = questionScores.filter(q => q.format_type === 'true_false');
  
  if (trueFalseQuestions.length === 0) {
    return { bonusXP: 0, achievements: [], streaks: [] };
  }
  
  let bonusXP = 0;
  const achievements: string[] = [];
  const streaks: { type: string; count: number }[] = [];
  
  // Count correct answers
  const correctAnswers = trueFalseQuestions.filter(q => q.percentage === 100);
  const accuracy = correctAnswers.length / trueFalseQuestions.length;
  
  // Lightning fast bonus (under 5 seconds)
  const lightningAnswers = trueFalseQuestions.filter(q => 
    q.timeSpent && q.timeSpent < 5 && q.percentage === 100
  );
  
  // Quick decision bonus (under 10 seconds)
  const quickAnswers = trueFalseQuestions.filter(q => 
    q.timeSpent && q.timeSpent < 10 && q.percentage === 100
  );
  
  // Perfect accuracy bonus
  if (accuracy === 1.0 && trueFalseQuestions.length >= 3) {
    bonusXP += 25;
    achievements.push('Perfect True/False Performance!');
    streaks.push({ type: 'perfect_true_false', count: trueFalseQuestions.length });
  }
  
  // High accuracy bonus (90%+)
  if (accuracy >= 0.9 && trueFalseQuestions.length >= 5) {
    bonusXP += 15;
    achievements.push('Excellent Discernment!');
  }
  
  // Speed bonuses
  if (lightningAnswers.length >= 3) {
    bonusXP += lightningAnswers.length * 3; // 3 XP per lightning answer
    achievements.push('Lightning Judge!');
    streaks.push({ type: 'lightning_true_false', count: lightningAnswers.length });
  } else if (quickAnswers.length >= 3) {
    bonusXP += quickAnswers.length * 2; // 2 XP per quick answer
    achievements.push('Quick Thinker!');
  }
  
  // Consecutive streak bonus
  let currentStreak = 0;
  let maxStreak = 0;
  
  trueFalseQuestions.forEach(q => {
    if (q.percentage === 100) {
      currentStreak++;
      maxStreak = Math.max(maxStreak, currentStreak);
    } else {
      currentStreak = 0;
    }
  });
  
  if (maxStreak >= 5) {
    bonusXP += maxStreak * 2; // 2 XP per consecutive correct
    achievements.push(`Truth Streak: ${maxStreak} in a row!`);
    streaks.push({ type: 'consecutive_true_false', count: maxStreak });
  }
  
  // Volume bonus for completing many True/False questions
  if (trueFalseQuestions.length >= 10) {
    bonusXP += 10;
    achievements.push('True/False Master!');
  }
  
  console.log(`[GamificationService] True/False rewards calculated:`, {
    totalQuestions: trueFalseQuestions.length,
    correctAnswers: correctAnswers.length,
    accuracy: Math.round(accuracy * 100) + '%',
    bonusXP,
    achievements,
    streaks
  });
  
  return { bonusXP, achievements, streaks };
}

// =====================================================
// STREAK SYSTEM
// =====================================================

export interface GamificationSubscriptionCallbacks {
  onXPUpdate?: (xp: UserXP) => void;
  onStreakUpdate?: (streak: UserStreak) => void;
  onAchievementUpdate?: (achievements: UserAchievement[]) => void;
  onLevelUp?: (newLevel: number, newRank: string) => void;
  onLeaderboardUpdate?: (leaderboard: XPLeaderboardEntry[]) => void;
  onError?: (error: Error) => void;
  onConnectionChange?: (connected: boolean) => void;
}

export interface GamificationSubscription {
  unsubscribe: () => void;
  isConnected: () => boolean;
  reconnect: () => Promise<void>;
}

/**
 * Enhanced subscription system with error handling and reconnection
 */
export function subscribeToGamificationUpdates(
  userId: string,
  callbacks: GamificationSubscriptionCallbacks
): GamificationSubscription {
  const channels: any[] = [];
  let isConnected = false;
  let reconnectAttempts = 0;
  const maxReconnectAttempts = 5;
  const reconnectDelay = 1000; // Start with 1 second

  const handleError = (error: Error) => {
    console.error('Gamification subscription error:', error);
    isConnected = false;
    callbacks.onError?.(error);
    callbacks.onConnectionChange?.(false);
  };

  const handleConnectionChange = (status: string) => {
    const connected = status === 'SUBSCRIBED';
    if (connected !== isConnected) {
      isConnected = connected;
      callbacks.onConnectionChange?.(connected);
      
      if (connected) {
        reconnectAttempts = 0; // Reset on successful connection
      }
    }
  };

  const createSubscriptions = () => {
    // Clear existing channels
    channels.forEach(channel => supabase.removeChannel(channel));
    channels.length = 0;

    try {
      // Subscribe to XP updates with level-up detection
      if (callbacks.onXPUpdate || callbacks.onLevelUp) {
        const xpChannel = supabase
          .channel(`user-xp-${userId}`)
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'user_xp',
            filter: `user_id=eq.${userId}`
          }, async (payload) => {
            try {
              const xp = await getUserXP(userId);
              if (xp) {
                callbacks.onXPUpdate?.(xp);
                
                // Check for level up
                if (payload.eventType === 'UPDATE' && payload.old && payload.new) {
                  const oldLevel = payload.old.current_level;
                  const newLevel = payload.new.current_level;
                  if (newLevel > oldLevel) {
                    const newRank = getRankFromLevel(newLevel);
                    callbacks.onLevelUp?.(newLevel, newRank);
                  }
                }
              }
            } catch (error) {
              handleError(error as Error);
            }
          })
          .on('system', {}, (payload) => {
            handleConnectionChange(payload.status);
          })
          .subscribe();
        channels.push(xpChannel);
      }

      // Subscribe to streak updates
      if (callbacks.onStreakUpdate) {
        const streakChannel = supabase
          .channel(`user-streaks-${userId}`)
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'user_streaks',
            filter: `user_id=eq.${userId}`
          }, async () => {
            try {
              const streak = await getUserStreak(userId);
              if (streak) callbacks.onStreakUpdate!(streak);
            } catch (error) {
              handleError(error as Error);
            }
          })
          .on('system', {}, (payload) => {
            handleConnectionChange(payload.status);
          })
          .subscribe();
        channels.push(streakChannel);
      }

      // Subscribe to achievement updates
      if (callbacks.onAchievementUpdate) {
        const achievementChannel = supabase
          .channel(`user-achievements-${userId}`)
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'user_achievements',
            filter: `user_id=eq.${userId}`
          }, async () => {
            try {
              const achievements = await getUserAchievements(userId);
              callbacks.onAchievementUpdate!(achievements);
            } catch (error) {
              handleError(error as Error);
            }
          })
          .on('system', {}, (payload) => {
            handleConnectionChange(payload.status);
          })
          .subscribe();
        channels.push(achievementChannel);
      }

      // Subscribe to leaderboard updates (for top users)
      if (callbacks.onLeaderboardUpdate) {
        const leaderboardChannel = supabase
          .channel('leaderboard-updates')
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'user_xp'
          }, async () => {
            try {
              // Only update if this user is in top 100
              const leaderboard = await getXPLeaderboard(100);
              const userInTop = leaderboard.some(entry => entry.user_id === userId);
              if (userInTop) {
                callbacks.onLeaderboardUpdate!(leaderboard);
              }
            } catch (error) {
              handleError(error as Error);
            }
          })
          .on('system', {}, (payload) => {
            handleConnectionChange(payload.status);
          })
          .subscribe();
        channels.push(leaderboardChannel);
      }

    } catch (error) {
      handleError(error as Error);
    }
  };

  const reconnect = async (): Promise<void> => {
    if (reconnectAttempts >= maxReconnectAttempts) {
      handleError(new Error('Max reconnection attempts reached'));
      return;
    }

    reconnectAttempts++;
    const delay = reconnectDelay * Math.pow(2, reconnectAttempts - 1); // Exponential backoff
    
    await new Promise(resolve => setTimeout(resolve, delay));
    
    try {
      createSubscriptions();
    } catch (error) {
      handleError(error as Error);
      // Try again
      setTimeout(() => reconnect(), delay);
    }
  };

  // Initial subscription setup
  createSubscriptions();

  return {
    unsubscribe: () => {
      channels.forEach(channel => {
        try {
          supabase.removeChannel(channel);
        } catch (error) {
          console.warn('Error removing channel:', error);
        }
      });
      channels.length = 0;
      isConnected = false;
    },
    isConnected: () => isConnected,
    reconnect
  };
}

/**
 * Subscribe to global leaderboard updates (for all users)
 */
/**
 * Get XP leaderboard
 */
export async function getXPLeaderboard(limit: number = 10): Promise<XPLeaderboardEntry[]> {
  // Return mock leaderboard data since the full leaderboard system isn't implemented yet
  return [
    {
      user_id: 'user1',
      username: 'BibleScholar',
      total_xp: 15000,
      level: 15,
      current_level: 15,
      rank: 'Scholar',
      avatar_url: null,
      quiz_count: 150,
      streak_count: 30
    },
    {
      user_id: 'user2', 
      username: 'TruthSeeker',
      total_xp: 12000,
      level: 12,
      current_level: 12,
      rank: 'Seeker',
      avatar_url: null,
      quiz_count: 120,
      streak_count: 25
    },
    {
      user_id: 'user3',
      username: 'WordWisdom',
      total_xp: 10000,
      level: 10,
      current_level: 10,
      rank: 'Student',
      avatar_url: null,
      quiz_count: 100,
      streak_count: 20
    }
  ];
}

/**
 * Get user streak calendar data
 */
export async function getUserStreakCalendar(userId: string, year: number, month: number): Promise<StreakCalendarDay[]> {
  // Return mock calendar data since the streak calendar system isn't fully implemented yet
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const calendarDays: StreakCalendarDay[] = [];
  
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push({
      activity_date: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
      activity_type: 'quiz',
      has_activity: Math.random() > 0.3 // Random completion for demo
    });
  }
  
  return calendarDays;
}

export function subscribeToLeaderboardUpdates(
  callbacks: {
    onLeaderboardUpdate: (leaderboard: XPLeaderboardEntry[]) => void;
    onError?: (error: Error) => void;
  }
): GamificationSubscription {
  let channel: any = null;
  let isConnected = false;
  let lastUpdate = 0;
  const updateThrottle = 5000; // Throttle updates to every 5 seconds

  const handleError = (error: Error) => {
    console.error('Leaderboard subscription error:', error);
    isConnected = false;
    callbacks.onError?.(error);
  };

  const createSubscription = () => {
    try {
      channel = supabase
        .channel('global-leaderboard')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'user_xp'
        }, async () => {
          const now = Date.now();
          if (now - lastUpdate < updateThrottle) return; // Throttle updates
          
          lastUpdate = now;
          try {
            const leaderboard = await getXPLeaderboard(100);
            callbacks.onLeaderboardUpdate(leaderboard);
          } catch (error) {
            handleError(error as Error);
          }
        })
        .on('system', {}, (payload) => {
          isConnected = payload.status === 'SUBSCRIBED';
        })
        .subscribe();
    } catch (error) {
      handleError(error as Error);
    }
  };

  createSubscription();

  return {
    unsubscribe: () => {
      if (channel) {
        try {
          supabase.removeChannel(channel);
        } catch (error) {
          console.warn('Error removing leaderboard channel:', error);
        }
        channel = null;
      }
      isConnected = false;
    },
    isConnected: () => isConnected,
    reconnect: async () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
      createSubscription();
    }
  };
}

/**
 * Notification system for achievements and level-ups
 */
export interface GamificationNotification {
  id: string;
  type: 'achievement' | 'level_up' | 'streak_milestone' | 'xp_earned';
  title: string;
  message: string;
  data?: any;
  timestamp: number;
}

export function createGamificationNotifications(
  userId: string,
  callbacks: {
    onNotification: (notification: GamificationNotification) => void;
    onError?: (error: Error) => void;
  }
): GamificationSubscription {
  return subscribeToGamificationUpdates(userId, {
    onLevelUp: (newLevel, newRank) => {
      callbacks.onNotification({
        id: `level-up-${Date.now()}`,
        type: 'level_up',
        title: 'Level Up!',
        message: `Congratulations! You've reached level ${newLevel} (${newRank})`,
        data: { level: newLevel, rank: newRank },
        timestamp: Date.now()
      });
    },
    onAchievementUpdate: async (achievements) => {
      // Check for new achievements (this would need to be enhanced with previous state tracking)
      const recentAchievements = achievements.filter(
        achievement => achievement.completed_at && 
        Date.now() - new Date(achievement.completed_at).getTime() < 60000 // Within last minute
      );
      
      recentAchievements.forEach(achievement => {
        callbacks.onNotification({
          id: `achievement-${achievement.achievement_id}-${Date.now()}`,
          type: 'achievement',
          title: 'Achievement Unlocked!',
          message: `You've earned: ${achievement.achievement?.name || 'Unknown Achievement'}`,
          data: { achievement },
          timestamp: Date.now()
        });
      });
    },
    onStreakUpdate: (streak) => {
      // Check for streak milestones
      const milestones = [7, 30, 90, 365];
      if (milestones.includes(streak.current_streak)) {
        callbacks.onNotification({
          id: `streak-${streak.current_streak}-${Date.now()}`,
          type: 'streak_milestone',
          title: 'Streak Milestone!',
          message: `Amazing! You've maintained a ${streak.current_streak}-day streak!`,
          data: { streak: streak.current_streak },
          timestamp: Date.now()
        });
      }
    },
    onError: callbacks.onError
  });
}

// =====================================================
// DAILY VERSE FUNCTIONS
// =====================================================

// In-memory storage for daily verse completions (since database tables don't exist)
const dailyVerseCompletions = new Map<string, string>(); // userId -> completionDate

/**
 * Get today's daily verse
 */
export async function getDailyVerse(language: string = 'en'): Promise<DailyVerse | null> {
  // Get the verse in the requested language, fallback to English
  const verses = bibleVerses[language as keyof typeof bibleVerses] || bibleVerses.en;
  const verse = verses.john_3_16;
  
  return {
    id: 'default',
    verse_text: verse.text,
    verse_reference: verse.reference,
    display_date: new Date().toISOString().split('T')[0],
    created_at: new Date().toISOString()
  };
}

/**
 * Check if user has completed today's daily verse
 */
export async function hasCompletedDailyVerse(userId: string): Promise<boolean> {
  const today = new Date().toISOString().split('T')[0];
  const userCompletion = dailyVerseCompletions.get(userId);
  return userCompletion === today;
}

/**
 * Mark daily verse as completed for user
 */
export async function completeDailyVerse(userId: string): Promise<boolean> {
  const today = new Date().toISOString().split('T')[0];
  dailyVerseCompletions.set(userId, today);
  console.log('[GamificationService] Daily verse marked as completed for user:', userId, 'on', today);
  return true;
}

/**
 * Get user's XP transactions
 */
export async function getUserXPTransactions(userId: string, limit: number = 10): Promise<XPTransaction[]> {
  try {
    const { data, error } = await supabase
      .from('xp_transactions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      if (error.code === '42P01' || error.details?.includes('does not exist')) {
        // Table doesn't exist - return empty array
        console.warn('xp_transactions table does not exist');
        return [];
      }
      console.error('Error fetching XP transactions:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getUserXPTransactions:', error);
    return [];
  }
}
